// Pricing · Quote detail — full read view of a deal-desk quote: summary, pricing
// (list vs quoted + grandfather version), configuration, fulfillment (PO/Stripe),
// and the approval history (approval_log). Actions: approve/reject (pending) or
// provision (approved). Data: GET /quotes/:id.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageHeader, Card, CardHead, Mono, Btn } from '@/components/shared/primitives';
import { Pill, TierPill } from '@/components/shared/badges';
import { Icon } from '@/components/shared/Icon';
import { AlertBanner } from '@/components/shared/AlertBanner';
import { ReasonDialog } from '@/components/shared/ReasonDialog';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { toast } from '@/components/shared/ToastProvider';
import { apiRequest } from '@/lib/supabase';
import { getPriceBook } from '@/lib/api/pricing';
import { PERSONA } from '@/lib/persona';

const SALES = PERSONA.sales.accent;
const SYM: Record<string, string> = { USD: '$', EUR: '€', GBP: '£' };

interface Approval { id: string; decision: string; reason: string | null; approver: string | null; created_at: string }
interface Quote {
  id: string; quote_number?: string; organization_id?: string | null; organization?: { name?: string } | null;
  scope?: string; tier?: string; config?: { vendorPool?: number; orgSeats?: number; modules?: string[]; packs?: Array<{ type: string; n: number }> };
  currency?: string; term_years?: number; list_price_monthly?: number; quoted_price_monthly?: number; discount_pct?: number;
  fulfillment?: string; po_number?: string | null; po_terms?: string | null; po_activation_policy?: string | null; ap_contact?: string | null;
  status?: string; price_version?: string; provisioned_license_id?: string | null; owner?: string | null;
  created_at?: string; approvals?: Approval[];
}

const QS: Record<string, { bg: string; fg: string }> = {
  approved: { bg: 'var(--ps-pill-green-bg)', fg: 'var(--ps-pill-green-tx)' }, 'pending-approval': { bg: 'var(--ps-pill-amber-bg)', fg: 'var(--ps-pill-amber-tx)' },
  'needs-approval': { bg: 'var(--ps-pill-red-bg)', fg: 'var(--ps-pill-red-tx)' }, provisioned: { bg: 'var(--ps-pill-blue-bg)', fg: 'var(--ps-pill-blue-tx)' },
  rejected: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)' }, draft: { bg: 'var(--ps-pill-gray-bg)', fg: 'var(--ps-pill-gray-tx)' },
};
const PENDING = new Set(['pending-approval', 'needs-approval']);

function KV({ k, children }: { k: string; children: React.ReactNode }) {
  return <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '11px 0', borderBottom: '1px solid var(--bg-muted)', fontSize: 13.5 }}><span style={{ color: 'var(--text-secondary)' }}>{k}</span><span style={{ fontWeight: 500 }}>{children}</span></div>;
}

export function QuoteDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [q, setQ] = useState<Quote | null>(null);
  const [activeVersion, setActiveVersion] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rejectOpen, setRejectOpen] = useState(false);
  const [provisionOpen, setProvisionOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true); setError(null);
    try {
      const data = await apiRequest<Quote>(`/quotes/${id}`);
      setQ(data);
      getPriceBook().then((pb) => setActiveVersion(pb.version)).catch(() => {});
    } catch (e) { setError(e instanceof Error ? e.message : 'Failed to load quote'); }
    finally { setLoading(false); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  // One request at a time. `busy` disables the buttons, but a state update only lands on the next
  // render, so two clicks inside the same tick would both send: the ref closes that gap (DF-34/DF-39).
  const inFlight = useRef(false);
  const act = async (verb: 'approve' | 'reject' | 'provision', body?: Record<string, unknown>) => {
    if (!id || inFlight.current) return;
    inFlight.current = true;
    setBusy(true);
    try {
      const res = await apiRequest<{ license_key?: string; idempotent?: boolean }>(`/quotes/${id}/${verb}`, { method: 'POST', body: JSON.stringify(body || {}) });
      const provisionBody = res?.license_key
        ? `License ${res.license_key} issued. Logged to the audit trail.`
        : 'This quote already had its license; no new license was issued.';
      toast({ kind: 'success', title: `Quote ${verb === 'approve' ? 'approved' : verb === 'reject' ? 'rejected' : 'provisioned'}`, body: verb === 'provision' ? provisionBody : 'Logged to the audit trail.' });
      setRejectOpen(false); setProvisionOpen(false);
      await load();
    } catch (e) {
      toast({ kind: 'error', title: 'Action failed', body: e instanceof Error ? e.message : 'Could not complete.' });
      if (verb === 'provision') {
        // A refusal here usually means another request is already provisioning this quote: close the
        // dialog (retrying is the wrong move) and show what the server holds now.
        setProvisionOpen(false);
        await load();
      }
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>Loading quote…</div>;
  if (error || !q) return <div style={{ padding: 24 }}><AlertBanner kind="error">{error || 'Quote not found'}</AlertBanner></div>;

  const sym = SYM[q.currency || 'USD'] || '$';
  const fmt = (n?: number) => sym + Number(n || 0).toLocaleString();
  const pending = PENDING.has(q.status || '');
  const years = q.term_years || 1;
  const annual = (q.quoted_price_monthly || 0) * 12;
  const stale = !!activeVersion && !!q.price_version && q.price_version !== activeVersion && (pending || q.status === 'draft');
  const st = QS[q.status || 'draft'] || QS.draft;
  const cfg = q.config || {};

  return (
    <div>
      <PageHeader
        breadcrumb={['Quotes', q.quote_number || 'Quote']}
        back onBack={() => navigate('/sales/quotes')}
        onCrumb={(i) => i === 0 && navigate('/sales/quotes')}
        title={<span style={{ fontFamily: 'var(--mono)' }}>{q.quote_number || q.id}</span>}
        subtitle={`${q.organization?.name || '—'} · ${q.tier || ''} · ${q.scope || 'org'} scope`}
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            {pending && <Btn kind="primary" accent="#16A34A" icon="check" disabled={busy} onClick={() => act('approve')}>Approve</Btn>}
            {pending && <Btn icon="x" disabled={busy} onClick={() => setRejectOpen(true)} style={{ color: 'var(--ps-pill-red-tx)', borderColor: 'var(--ps-pill-red-bg)', background: 'var(--ps-pill-red-bg)' }}>Reject</Btn>}
            {q.status === 'approved' && <Btn kind="primary" accent={SALES} icon="rocket" disabled={busy} onClick={() => setProvisionOpen(true)}>Provision</Btn>}
          </div>
        }
      />

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
        <Pill bg={st.bg} fg={st.fg}>{(q.status || 'draft').replace('-', ' ')}</Pill>
        {q.price_version && <Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">{q.price_version}</Pill>}
        {stale && <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">stale — re-price</Pill>}
        {q.provisioned_license_id && <Btn sm icon="external-link" onClick={() => navigate(`/sales/licenses/${q.provisioned_license_id}`)}>View issued license</Btn>}
      </div>

      {stale && <div style={{ marginBottom: 16 }}><AlertBanner kind="warning">This quote was priced on <strong>{q.price_version}</strong>, which is no longer the active rate card (<strong>{activeVersion}</strong>). Re-price before approving.</AlertBanner></div>}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}>
        <Card>
          <CardHead title="Summary" />
          <div style={{ padding: '6px 20px 14px' }}>
            <KV k="Account">{q.organization?.name || '—'}</KV>
            <KV k="Tier"><TierPill tier={q.tier || 'trial'} /></KV>
            <KV k="Scope">{q.scope || 'org'}</KV>
            <KV k="Currency">{q.currency || 'USD'}</KV>
            <KV k="Term">{years} year{years > 1 ? 's' : ''}</KV>
            <KV k="Created">{q.created_at ? new Date(q.created_at).toLocaleString() : '—'}</KV>
          </div>
        </Card>
        <Card>
          <CardHead title="Pricing" sub="Quoted vs the rate card" />
          <div style={{ padding: '6px 20px 14px' }}>
            <KV k="List price / mo">{fmt(q.list_price_monthly)}</KV>
            <KV k="Quoted price / mo"><span style={{ fontWeight: 700 }}>{fmt(q.quoted_price_monthly)}</span></KV>
            <KV k="Discount">{q.discount_pct ? <Pill bg="var(--ps-pill-amber-bg)" fg="var(--ps-pill-amber-tx)">-{Math.round(q.discount_pct)}%</Pill> : <span style={{ color: 'var(--text-muted)' }}>list</span>}</KV>
            <KV k="Annual (×12)">{fmt(annual)}</KV>
            <KV k="Contract value">{fmt(annual * years)}</KV>
            <KV k="Rate-card version">{q.price_version || '—'}</KV>
          </div>
        </Card>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}>
        <Card>
          <CardHead title="Configuration" />
          <div style={{ padding: '6px 20px 14px' }}>
            <KV k="Vendor pool"><Mono>{(cfg.vendorPool ?? 0).toLocaleString()}</Mono></KV>
            <KV k="Org seats"><Mono>{cfg.orgSeats ?? 0}</Mono></KV>
            <KV k="Modules">{cfg.modules && cfg.modules.length ? cfg.modules.join(', ') : <span style={{ color: 'var(--text-muted)' }}>none</span>}</KV>
            <KV k="Expansion packs">{cfg.packs && cfg.packs.length ? cfg.packs.map((p) => `${p.type}×${p.n}`).join(', ') : <span style={{ color: 'var(--text-muted)' }}>none</span>}</KV>
          </div>
        </Card>
        <Card>
          <CardHead title="Fulfillment" />
          <div style={{ padding: '6px 20px 14px' }}>
            {q.fulfillment === 'po' ? (
              <>
                <KV k="Method"><Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">Purchase Order</Pill></KV>
                <KV k="PO number">{q.po_number || <span style={{ color: 'var(--text-muted)' }}>—</span>}</KV>
                <KV k="Payment terms">{q.po_terms || '—'}</KV>
                <KV k="Activation policy">{q.po_activation_policy || '—'}</KV>
                <KV k="AP contact">{q.ap_contact || '—'}</KV>
              </>
            ) : (
              <>
                <KV k="Method"><Pill bg="var(--ps-pill-blue-bg)" fg="var(--ps-pill-blue-tx)">Card / Stripe</Pill></KV>
                <KV k="Billing">Immediate subscription on success</KV>
              </>
            )}
          </div>
        </Card>
      </div>

      <Card style={{ overflow: 'hidden' }}>
        <CardHead title="Approval history" sub="Every decision is logged to the audit trail" />
        <div style={{ padding: '8px 18px 14px' }}>
          {(q.approvals || []).length === 0 ? (
            <div style={{ padding: '18px 0', textAlign: 'center', color: 'var(--text-muted)', fontSize: 13 }}>No approval decisions yet.</div>
          ) : (
            [...(q.approvals || [])].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()).map((a, i, arr) => {
              const ok = a.decision === 'approve';
              return (
                <div key={a.id} style={{ display: 'flex', gap: 12, padding: '11px 0', borderBottom: i < arr.length - 1 ? '1px solid var(--bg-muted)' : 'none' }}>
                  <div style={{ width: 28, height: 28, borderRadius: 7, background: ok ? 'var(--ps-pill-green-bg)' : 'var(--ps-pill-red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={ok ? 'check' : 'x'} size={14} color={ok ? 'var(--ps-pill-green-tx)' : 'var(--ps-pill-red-tx)'} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize', color: ok ? 'var(--ps-pill-green-tx)' : 'var(--ps-pill-red-tx)' }}>{a.decision}d</div>
                    {a.reason && <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', marginTop: 1 }}>{a.reason}</div>}
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 2 }}>{new Date(a.created_at).toLocaleString()}</div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </Card>

      <ReasonDialog open={rejectOpen} title="Reject quote?" body="The quote is marked rejected and the reason is logged to the audit trail." confirmLabel="Reject quote" danger onConfirm={(reason) => act('reject', { reason })} onCancel={() => setRejectOpen(false)} />
      <ConfirmDialog
        open={provisionOpen}
        title="Provision this quote?"
        body={`Issues the signed license for ${q.organization?.name || 'the quote\'s account'} (${q.tier || 'tier'}, ${years}-year term) from this quote and marks the quote provisioned. It is issued once: provisioning this quote again returns that license, it does not issue another.`}
        confirmLabel="Provision"
        busy={busy}
        onConfirm={() => act('provision')}
        onCancel={() => setProvisionOpen(false)}
      />
    </div>
  );
}

export default QuoteDetail;
